import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { reviewsAPI } from '../../services/api';
import { toast } from 'sonner';
import { ArrowLeft, Star, Building, Clock, Check, AlertTriangle, Flag, Send, Loader2 } from 'lucide-react';

const EmployeeReviewDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selfAssessment, setSelfAssessment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Flag dialog state
  const [flagDialogOpen, setFlagDialogOpen] = useState(false);
  const [flagFieldName, setFlagFieldName] = useState('');
  const [flagMessage, setFlagMessage] = useState('');
  const [flagging, setFlagging] = useState(false);

  useEffect(() => {
    fetchReview();
  }, [id]);

  const fetchReview = async () => {
    try {
      const res = await reviewsAPI.getReview(id);
      setReview(res.data);
      if (res.data.self_assessment) {
        setSelfAssessment(res.data.self_assessment);
      }
    } catch (error) {
      console.error('Error fetching review:', error);
      toast.error('Failed to load review');
      navigate('/employee/reviews');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitSelfAssessment = async () => {
    if (!selfAssessment.trim()) {
      toast.error('Please enter your self-assessment');
      return;
    }
    
    setSubmitting(true);
    try {
      await reviewsAPI.addSelfAssessment(id, selfAssessment);
      toast.success('Self-assessment submitted');
      fetchReview();
    } catch (error) {
      toast.error('Failed to submit self-assessment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAcknowledge = async () => {
    setSubmitting(true);
    try {
      await reviewsAPI.acknowledgeReview(id);
      toast.success('Review acknowledged');
      fetchReview();
    } catch (error) {
      toast.error('Failed to acknowledge review');
    } finally {
      setSubmitting(false);
    }
  };

  const openFlagDialog = (fieldName) => {
    setFlagFieldName(fieldName);
    setFlagMessage('');
    setFlagDialogOpen(true);
  };

  const handleSubmitFlag = async () => {
    if (!flagMessage.trim()) {
      toast.error('Please explain the issue');
      return;
    }
    
    setFlagging(true);
    try {
      await reviewsAPI.flagReview({
        review_id: id,
        field_name: flagFieldName,
        message: flagMessage
      });
      toast.success('Flag submitted');
      setFlagDialogOpen(false);
      fetchReview();
    } catch (error) {
      toast.error('Failed to submit flag');
    } finally {
      setFlagging(false);
    }
  };

  const getCadenceBadge = (cadence) => {
    const colors = {
      'one-time': 'bg-[#F5F8FF] text-[#5B6472]',
      'weekly': 'bg-blue-500/20 text-blue-400',
      'monthly': 'bg-purple-500/20 text-purple-400',
      'quarterly': 'bg-green-500/20 text-green-400'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-sm ${colors[cadence] || colors['one-time']}`}>
        {cadence?.charAt(0).toUpperCase() + cadence?.slice(1)}
      </span>
    );
  };

  const isFieldFlagged = (fieldName) => {
    return review.flags?.some(f => f.field_name === fieldName);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#1B5AFF]" />
        </div>
      </DashboardLayout>
    );
  }

  if (!review) {
    return null;
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-4xl" data-testid="employee-review-detail">
        {/* Header */}
        <div className="animate-fade-in">
          <Button
            variant="ghost"
            onClick={() => navigate('/employee/reviews')}
            className="text-[#5B6472] hover:text-[#0A0F1E] mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Reviews
          </Button>
        </div>

        {/* Employer Info Card */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-[#F5F8FF] flex items-center justify-center">
                {review.employer_logo ? (
                  <img src={review.employer_logo} alt="" className="w-16 h-16 rounded-full object-cover" />
                ) : (
                  <Building className="w-8 h-8 text-[#5B6472]" />
                )}
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-[#0A0F1E]">
                  Performance Review
                </h1>
                <p className="text-[#5B6472]">From {review.employer_name || 'Employer'}</p>
                <div className="flex items-center gap-2 mt-2">
                  {getCadenceBadge(review.review_type)}
                  {review.status === 'pending' ? (
                    <span className="px-3 py-1 rounded-full bg-amber-500/20 text-amber-400 text-sm flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Awaiting Your Response
                    </span>
                  ) : (
                    <span className="px-3 py-1 rounded-full bg-green-500/20 text-green-400 text-sm flex items-center gap-1">
                      <Check className="w-3 h-3" />
                      Acknowledged
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="text-right">
              <p className="text-[#5B6472] text-sm">Review Date</p>
              <p className="text-[#0A0F1E] font-medium">{new Date(review.created_at).toLocaleDateString()}</p>
            </div>
          </div>
        </Card>

        {/* Performance Rating */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-[#0A0F1E] mb-4">Your Performance Rating</h2>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <Star 
                      key={rating}
                      className={`w-8 h-8 ${
                        rating <= review.performance_rating 
                          ? 'fill-[#1B5AFF] text-[#1B5AFF]' 
                          : 'text-[#5B6472]'
                      }`} 
                    />
                  ))}
                </div>
                <span className="text-2xl font-bold text-[#0A0F1E]">{review.performance_rating}/5</span>
              </div>
            </div>
            {!isFieldFlagged('performance_rating') && review.status === 'pending' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => openFlagDialog('Performance Rating')}
                className="text-[#5B6472] hover:text-red-400"
              >
                <Flag className="w-4 h-4" />
              </Button>
            )}
          </div>
        </Card>

        {/* Review Content */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Key Achievements */}
          {review.key_achievements && (
            <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-[#0A0F1E]">Key Achievements</h3>
                {!isFieldFlagged('key_achievements') && review.status === 'pending' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openFlagDialog('Key Achievements')}
                    className="text-[#5B6472] hover:text-red-400"
                  >
                    <Flag className="w-4 h-4" />
                  </Button>
                )}
              </div>
              <p className="text-[#0A0F1E] whitespace-pre-wrap">{review.key_achievements}</p>
            </Card>
          )}

          {/* Areas to Improve */}
          {review.areas_to_improve && (
            <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-[#0A0F1E]">Areas to Improve</h3>
                {!isFieldFlagged('areas_to_improve') && review.status === 'pending' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openFlagDialog('Areas to Improve')}
                    className="text-[#5B6472] hover:text-red-400"
                  >
                    <Flag className="w-4 h-4" />
                  </Button>
                )}
              </div>
              <p className="text-[#0A0F1E] whitespace-pre-wrap">{review.areas_to_improve}</p>
            </Card>
          )}
        </div>

        {/* Goals */}
        {review.goals_next_period && (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-[#0A0F1E]">Goals for Next Period</h3>
              {!isFieldFlagged('goals_next_period') && review.status === 'pending' && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => openFlagDialog('Goals for Next Period')}
                  className="text-[#5B6472] hover:text-red-400"
                >
                  <Flag className="w-4 h-4" />
                </Button>
              )}
            </div>
            <p className="text-[#0A0F1E] whitespace-pre-wrap">{review.goals_next_period}</p>
          </Card>
        )}

        {/* Manager Notes (if shared) */}
        {review.manager_notes && review.share_manager_notes && (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
            <h3 className="text-lg font-semibold text-[#0A0F1E] mb-3">Manager Notes</h3>
            <p className="text-[#0A0F1E] whitespace-pre-wrap">{review.manager_notes}</p>
          </Card>
        )}

        {/* Flags */}
        {review.flags && review.flags.length > 0 && (
          <Card className="bg-[#FFFFFF] border border-red-500/30 rounded-xl p-6 animate-fade-in">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              <h3 className="text-lg font-semibold text-[#0A0F1E]">Your Disputed Fields</h3>
            </div>
            <div className="space-y-3">
              {review.flags.map((flag) => (
                <div key={flag.id} className="p-4 rounded-xl bg-red-500/10 border border-red-500/20">
                  <p className="text-red-400 font-medium text-sm mb-1">Disputed: {flag.field_name}</p>
                  <p className="text-[#0A0F1E] text-sm">{flag.employee_message}</p>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Self Assessment */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
          <h3 className="text-lg font-semibold text-[#0A0F1E] mb-4">Your Self-Assessment</h3>
          <Textarea
            value={selfAssessment}
            onChange={(e) => setSelfAssessment(e.target.value)}
            placeholder="Share your thoughts on this review, your accomplishments, and your goals..."
            className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl min-h-[150px]"
            disabled={review.status === 'acknowledged'}
          />
          {review.status === 'pending' && (
            <Button
              onClick={handleSubmitSelfAssessment}
              disabled={submitting}
              className="mt-4 bg-[#F5F8FF] hover:bg-[#F5F8FF]/80 text-[#0A0F1E] rounded-xl"
            >
              {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
              {review.self_assessment ? 'Update Response' : 'Submit Response'}
            </Button>
          )}
        </Card>

        {/* Acknowledge Button */}
        {review.status === 'pending' && (
          <Card className="bg-[#FFFFFF] border border-green-500/30 rounded-xl p-6 animate-fade-in">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-[#0A0F1E]">Ready to acknowledge?</h3>
                <p className="text-[#5B6472] text-sm">
                  Acknowledging confirms you've read this review. You can still add a self-assessment after.
                </p>
              </div>
              <Button
                onClick={handleAcknowledge}
                disabled={submitting}
                className="bg-green-500 hover:bg-green-600 hover:bg-green-700 text-[#FFFFFF] rounded-xl"
                data-testid="acknowledge-review-btn"
              >
                {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
                Acknowledge Review
              </Button>
            </div>
          </Card>
        )}

        {/* Acknowledged Info */}
        {review.status === 'acknowledged' && review.acknowledged_at && (
          <div className="text-center text-green-400 text-sm flex items-center justify-center gap-2">
            <Check className="w-4 h-4" />
            You acknowledged this review on {new Date(review.acknowledged_at).toLocaleDateString()}
          </div>
        )}
      </div>

      {/* Flag Dialog */}
      <Dialog open={flagDialogOpen} onOpenChange={setFlagDialogOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#E8EDF5] text-[#0A0F1E]">
          <DialogHeader>
            <DialogTitle>Flag "{flagFieldName}"</DialogTitle>
            <DialogDescription className="text-[#5B6472]">
              If you believe this field is inaccurate, please explain the issue. Your employer will be notified.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[#0A0F1E]">What's incorrect?</Label>
              <Textarea
                value={flagMessage}
                onChange={(e) => setFlagMessage(e.target.value)}
                placeholder="Explain why you believe this information is incorrect..."
                className="mt-2 bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl min-h-[100px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFlagDialogOpen(false)} className="border-[#E8EDF5] text-[#0A0F1E]">
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitFlag}
              disabled={flagging}
              className="bg-red-500 hover:bg-red-600 hover:bg-red-700 text-[#FFFFFF]"
            >
              {flagging ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Flag className="mr-2 h-4 w-4" />}
              Submit Flag
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default EmployeeReviewDetail;
